import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Alert } from '@app/ui/alert';
import { Label } from '@app/ui/label';
import { Text } from '@app/ui/forms';
import { Tooltip } from '@app/ui/tooltip';
import { error as toastError } from '@app/snackbars';

import { RULES_DRY_RUN_GET_ONE, RULE_TEST_RUN } from '../gql';
import { DryRunCaseResult, RuleDryRun, RuleTestOutcome, RuleTestSource } from '../types';
import { SEVERITY_LABEL_COLOR } from '../constants';
import { getGraphQLErrorMessage, getRuleTestOutcome, parseJsonObject } from '../utils';

export interface RuleTestPanelProps {
  readonly ruleRowId: number;
  readonly ruleId: string;
}

const POLL_INTERVAL_MS = 2000;
// 120s, not 80s: a single-rule run is still one model call plus the ECS consumer's own queue wait,
// and the shorter deadline reported a timeout for runs that then completed.
const POLL_MAX_ATTEMPTS = 60;
const POLL_DEADLINE_MS = POLL_INTERVAL_MS * POLL_MAX_ATTEMPTS;

const OUTCOME_CHIP: Record<RuleTestOutcome, { labelKey: string; color: string }> = {
  fired: { labelKey: 'rules.test.fired', color: 'success' },
  notFired: { labelKey: 'rules.test.notFired', color: 'grey' },
  unevaluable: { labelKey: 'rules.test.couldNotEvaluate', color: 'warning' },
};

export const RuleTestPanel = ({ ruleRowId, ruleId }: RuleTestPanelProps) => {
  const { t } = useTranslation('common');

  const [source, setSource] = useState<RuleTestSource>(RuleTestSource.cleanProfile);
  const [customProfileJson, setCustomProfileJson] = useState('{}');
  const [applicationIdInput, setApplicationIdInput] = useState('');
  const [realProfileConfirmed, setRealProfileConfirmed] = useState(false);
  const [dryRunId, setDryRunId] = useState<string | null>(null);
  const [result, setResult] = useState<RuleDryRun | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  const [testRun, { loading: starting }] = useMutation<{ ruleTestRun: RuleDryRun }>(RULE_TEST_RUN);

  const { data: pollData, stopPolling } = useQuery<{ rulesDryRunGetOne: RuleDryRun | null }>(RULES_DRY_RUN_GET_ONE, {
    variables: { dryRunId },
    skip: !dryRunId || !!result || timedOut,
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: 'network-only',
  });

  // Cleared on unmount AND whenever `dryRunId` changes, so a second run never inherits the first
  // run's timer. (Wall-clock rather than a poll counter — see `DryRunBench`.)
  useEffect(() => {
    if (!dryRunId || result) {
      return undefined;
    }

    const timer = setTimeout(() => setTimedOut(true), POLL_DEADLINE_MS);

    return () => clearTimeout(timer);
  }, [dryRunId, result]);

  // The row is DELETE-ON-READ once terminal, so capture the first terminal response and stop.
  useEffect(() => {
    const row = pollData?.rulesDryRunGetOne;

    if (row && (row.status === 'completed' || row.status === 'failed')) {
      setResult(row);
      stopPolling();
    }
  }, [pollData, stopPolling]);

  const handleRun = async () => {
    let profile: Record<string, unknown> | undefined;
    let applicationId: number | undefined;

    if (source === RuleTestSource.customProfile) {
      const parsed = parseJsonObject(customProfileJson);

      if (!parsed) {
        toastError(t('rules.test.invalidProfileJson'));

        return;
      }

      profile = parsed;
    }

    if (source === RuleTestSource.application) {
      const parsedId = Number(applicationIdInput);

      if (!applicationIdInput || Number.isNaN(parsedId)) {
        toastError(t('rules.test.invalidApplicationId'));

        return;
      }

      applicationId = parsedId;
    }

    try {
      const { data } = await testRun({ variables: { input: { ruleRowId, source, profile, applicationId } } });

      if (data?.ruleTestRun?.dryRunId) {
        setResult(null);
        setTimedOut(false);
        setDryRunId(data.ruleTestRun.dryRunId);
      }
    } catch (e) {
      const reason = getGraphQLErrorMessage(e);

      toastError(reason ? t('rules.test.startFailed', { reason }) : t('general.error.somethingWentWrong'));
    }
  };

  const handleReset = () => {
    setDryRunId(null);
    setResult(null);
    setTimedOut(false);
  };

  const running = !!dryRunId && !result && !timedOut;
  // Per-rule Test is watchlist-only (G5 — there is no colour equivalent), so `payload.cases[]`
  // always holds `DryRunCaseResult` here, never the colour shape.
  const caseResult = (result?.payload?.cases?.[0] as DryRunCaseResult | undefined) ?? null;
  const finding = caseResult?.findings?.find(entry => entry.ruleId === ruleId) ?? null;
  // The two halves of one case, joined by `caseId` — see `DryRunBench`.
  const skip =
    result?.cases?.find(entry => entry.caseId === caseResult?.caseId)?.skipped.find(s => s.ruleId === ruleId) ?? null;
  const outcome = getRuleTestOutcome(!!finding, skip);
  const runFailed = !!result && (result.status === 'failed' || caseResult?.status === 'failed' || !caseResult);
  const runDisabled = source === RuleTestSource.application && !realProfileConfirmed;

  return (
    <Paper variant="outlined" elevation={0} sx={{ borderRadius: '16px', p: { xs: 2, sm: 3 }, mt: 3 }}>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t('rules.test.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('rules.test.intro')}
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        {t('rules.test.simulationBanner')}
      </Alert>

      {!running && !result && (
        <>
          <Text
            select
            label={t('rules.test.sourceLabel')}
            helperText={t('rules.test.sourceHelperText')}
            value={source}
            // Scoped to the real-profile source: left set, switching away and back would arm the
            // run without a second acknowledgement.
            onChange={event => {
              setSource(event.target.value as RuleTestSource);
              setRealProfileConfirmed(false);
            }}
            options={[
              { id: RuleTestSource.cleanProfile, name: t('rules.test.source.cleanProfile') ?? '' },
              { id: RuleTestSource.customProfile, name: t('rules.test.source.customProfile') ?? '' },
              { id: RuleTestSource.application, name: t('rules.test.source.application') ?? '' },
            ]}
            sx={{ mb: 2 }}
          />

          {source === RuleTestSource.customProfile && (
            <Text
              multiline
              rows={6}
              label={t('rules.test.customProfileJsonLabel')}
              helperText={t('rules.test.customProfileJsonHelperText')}
              value={customProfileJson}
              onChange={event => setCustomProfileJson(event.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          {source === RuleTestSource.application && (
            <>
              <Text
                label={t('rules.test.applicationIdLabel')}
                helperText={t('rules.test.applicationIdHelperText')}
                value={applicationIdInput}
                onChange={event => {
                  setApplicationIdInput(event.target.value);
                  setRealProfileConfirmed(false);
                }}
                sx={{ mb: 2 }}
              />

              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('rules.test.realProfileConfirm')}
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" color="primaryLight" onClick={() => setRealProfileConfirmed(true)}>
                  {t('rules.test.realProfileConfirmButton')}
                </Button>
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title={runDisabled ? t('rules.test.runDisabledTooltip') ?? undefined : undefined}>
              <span>
                <LoadingButton
                  loading={starting}
                  variant="contained"
                  color="primary"
                  disabled={runDisabled}
                  onClick={handleRun}
                >
                  {t('rules.test.runButton')}
                </LoadingButton>
              </span>
            </Tooltip>
          </Box>
        </>
      )}

      {running && (
        <Typography variant="body1" color="text.secondary">
          {t('rules.test.runningNotice')}
        </Typography>
      )}

      {timedOut && !result && (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('rules.test.timedOutNotice', { seconds: POLL_DEADLINE_MS / 1000 })}
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" color="primary" onClick={handleReset}>
              {t('rules.test.retryButton')}
            </Button>
          </Box>
        </Box>
      )}

      {result && (
        <Box>
          {runFailed ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('rules.test.unevaluable', {
                reason: caseResult?.failure?.reason ?? result.failureReason ?? t('rules.test.unknownReason'),
              })}
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: outcome === 'unevaluable' ? 1 : 2 }}>
                <Label
                  text={t(OUTCOME_CHIP[outcome].labelKey)}
                  color={OUTCOME_CHIP[outcome].color}
                  sx={{ lineHeight: 'normal' }}
                />
                {finding && (
                  <Label
                    text={t(`rules.severity.option.${finding.severity}`, finding.severity)}
                    color={SEVERITY_LABEL_COLOR[finding.severity] ?? 'grey'}
                    sx={{ lineHeight: 'normal' }}
                  />
                )}
              </Box>

              {outcome === 'unevaluable' && skip && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t(`rules.skipReason.${skip.reason}`)}
                  {skip.detail && (
                    <Box component="span" sx={{ fontFamily: 'monospace', ml: 0.5 }}>
                      {skip.detail}
                    </Box>
                  )}
                </Typography>
              )}
            </>
          )}

          {finding && (
            <>
              <Typography variant="h4" sx={{ mb: 1 }}>
                {t('rules.test.triggersTitle')}
              </Typography>
              {finding.triggers.map(trigger => (
                <Typography key={`${trigger.question}-${trigger.answer}`} variant="body1" sx={{ mb: 0.5 }}>
                  {t('rules.test.triggerRow', {
                    question: trigger.question,
                    answer: trigger.answer,
                    appliesTo: t(`rules.editor.appliesTo.${trigger.appliesTo}`, trigger.appliesTo),
                  })}
                </Typography>
              ))}

              <Divider sx={{ my: 2 }} />

              <Typography variant="h4" sx={{ mb: 1 }}>
                {t('rules.test.justificationTitle')}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {finding.justification}
              </Typography>
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="primaryLight" onClick={handleReset}>
              {t('rules.test.runAgainButton')}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};
